/**
 * @fileoverview this component contains each of the wrapper components needed to properly render a React
 * component within the Braze dashboard. See `reactApp.tsx` which uses this to properly mount all other components.
 */

import React, { lazy, Suspense } from "react";
import { I18nextProvider } from "react-i18next";

import { QueryClientProvider } from "@tanstack/react-query";

import { queryClient } from "@lib/queryClient";
// eslint-disable-next-line local-eslint-rules/no-direct-view-model-access
import viewModel from "@lib/viewModel";

import { CurrentDeveloperProvider } from "@services/useDeveloper";

import { BrazeDashboardConfigProvider } from "@components/BrazeDashboardConfigProvider";
import { ElevatedAccessToasts } from "@components/ElevatedAccessToasts/ElevatedAccessToasts";
import { FeatureFlagProvider } from "@components/FeatureFlagProvider";
import { GlobalStateProvider } from "@components/GlobalStateProvider";
import { PlatformThemeProvider } from "@components/PlatformThemeProvider";
import RootComponent from "@components/RootComponent";
import { ViewModelProvider } from "@components/ViewModelProvider";

import i18n from "../../i18n";
import type { BaseDashboardComponentProps } from "./types";

// Dynamically import ViewModelClassesProvider.
// ViewModelClassesProvider is needed for loading GrapesJS editor in the editor domain.
// However, using it for CKEditor in the editor domain causes issues such as jquery is not
// defined in the iframe window. For consumers that do not need the ViewModelClassesProvider,
// it should not be imported.
const ViewModelClassesProvider = lazy(() => import("@components/ViewModelClassesProvider"));

/**
 * This is used to render in GrapesJS, and fetch error modals. Add any new shared providers to BaseKnockoutComponent as well
 *
 * @note you will find no ts-imports of this component because they are all dynamically
 * imported into various js-only packs. You will have to do a global find for the import
 * specifier to find instances (`@components/BaseDashboardComponent`)
 */
export const BaseDashboardComponent = ({
  children,
  componentName,
  useViewModelClassesProvider = false,
}: BaseDashboardComponentProps): React.ReactElement => {
  const component = <RootComponent featureId={componentName}>{children}</RootComponent>;
  const componentWithDeveloper = <CurrentDeveloperProvider>{component}</CurrentDeveloperProvider>;
  const content = (
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <ElevatedAccessToasts>
          <ViewModelProvider model={viewModel}>
            <FeatureFlagProvider>
              {useViewModelClassesProvider ? componentWithDeveloper : component}
            </FeatureFlagProvider>
          </ViewModelProvider>
        </ElevatedAccessToasts>
      </I18nextProvider>
    </QueryClientProvider>
  );

  return (
    <BrazeDashboardConfigProvider>
      <PlatformThemeProvider>
        <GlobalStateProvider>
          {useViewModelClassesProvider ? (
            <Suspense fallback={<div>Loading...</div>}>
              <ViewModelClassesProvider>{content}</ViewModelClassesProvider>
            </Suspense>
          ) : (
            content
          )}
        </GlobalStateProvider>
      </PlatformThemeProvider>
    </BrazeDashboardConfigProvider>
  );
};
